import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Sparkles, Lock, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { styleFor, tintForNumber } from "../lib/personalize";
import { api } from "../lib/api";

/**
 * PersonalizePicker
 *  - Fetches 3 fresh random 3-digit numbers + 3 education images from the server
 *    (POST /api/auth/personalize-options). The server also remembers what it
 *    offered so it can validate the user's selection.
 *  - Calls onSubmit({ lucky_number, avatar_id }) when "Complete Registration" is
 *    tapped. `lucky_number` is a 3-digit string (e.g. "042").
 *  - Fully controlled: parent handles busy state + API call.
 */
export default function PersonalizePicker({
  title = "Complete Your Registration",
  subtitle = "Choose your recovery number and image",
  helper = "Select one number and one image. These help you recover your account if you forget your password.",
  submitLabel = "Complete Registration",
  initial = null,        // { lucky_number, avatar_id }
  busy = false,
  onSubmit,
}) {
  const [options, setOptions] = useState({ lucky_numbers: [], avatars: [] });
  const [loading, setLoading] = useState(false);
  const [num, setNum] = useState(initial?.lucky_number ?? null);
  const [avatarId, setAvatarId] = useState(initial?.avatar_id ?? null);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const r = await api.personalizeOptions();
      setOptions({
        lucky_numbers: (r.lucky_numbers || []).map((n) => (typeof n === "string" ? { value: n } : n)),
        avatars: r.avatars || [],
      });
      // Deselect if a previous choice is no longer in the offered set.
      setNum((prev) => (prev && (r.lucky_numbers || []).some((n) => (n.value || n) === prev) ? prev : null));
      setAvatarId((prev) => (prev && (r.avatars || []).some((a) => a.id === prev) ? prev : null));
    } catch (e) {
      toast.error("Could not load recovery options. Please retry.");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchOptions(); }, []);

  const canSubmit = num !== null && !!avatarId && !busy && !loading;

  const submit = () => {
    if (num === null) return toast.error("Pick one recovery number");
    if (!avatarId) return toast.error("Pick one recovery image");
    onSubmit?.({ lucky_number: num, avatar_id: avatarId });
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 sm:p-8" data-testid="personalize-card">
      {/* Heading */}
      <div className="text-center">
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900">{title}</h2>
        <p className="mt-2 font-semibold text-slate-800">{subtitle}</p>
        <p className="mt-1 text-sm text-slate-500 max-w-lg mx-auto">{helper}</p>
      </div>

      {/* Step 1 — Recovery Number */}
      <section className="mt-6 rounded-2xl border border-slate-100 bg-slate-50/40 p-4 sm:p-5" data-testid="lucky-section">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="h-8 w-8 rounded-full bg-emerald-700 text-white grid place-items-center font-bold text-sm">1</span>
            <div>
              <h3 className="font-display font-bold text-slate-900 text-base">Choose Your Recovery Number</h3>
              <p className="text-xs text-slate-500">Select exactly one 3-digit number below</p>
            </div>
          </div>
          <button type="button" onClick={fetchOptions} disabled={loading || busy}
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="personalize-refresh">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""}/> New options
          </button>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4">
          {loading && options.lucky_numbers.length === 0 && [0,1,2].map((i) => (
            <div key={i} className="h-32 rounded-2xl border border-slate-100 bg-slate-100/60 animate-pulse"/>
          ))}
          {options.lucky_numbers.map((n, idx) => {
            const selected = num === n.value;
            const t = tintForNumber(n.value, idx);
            return (
              <button
                type="button"
                key={n.value}
                onClick={() => setNum(n.value)}
                data-testid={`lucky-num-${n.value}`}
                aria-pressed={selected}
                className={[
                  "relative rounded-2xl border transition-all p-3 sm:p-4 text-center flex flex-col items-center gap-1.5 min-h-[6rem]",
                  t.bg, t.borderColor,
                  selected ? `ring-2 ${t.ringColor} border-transparent shadow-md scale-[1.01]` : "hover:shadow-sm hover:-translate-y-0.5",
                ].join(" ")}
              >
                <div className="relative h-14 w-14 sm:h-16 sm:w-16 grid place-items-center">
                  <span aria-hidden className="absolute inset-0 opacity-70 [background:conic-gradient(from_45deg,transparent_0_10deg,currentColor_10deg_12deg,transparent_12deg_45deg)] text-slate-300 rounded-full mix-blend-multiply"/>
                  <span className={`relative font-mono font-extrabold text-3xl sm:text-4xl leading-none tracking-widest ${t.numColor}`}>{n.value}</span>
                </div>
                <span
                  className={[
                    "mt-1 h-5 w-5 rounded-full grid place-items-center border-2 transition-colors",
                    selected ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 bg-white",
                  ].join(" ")}
                >
                  {selected && <CheckCircle2 size={14} className="text-white"/>}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 2 — Recovery Image */}
      <section className="mt-5 rounded-2xl border border-slate-100 bg-slate-50/40 p-4 sm:p-5" data-testid="avatar-section">
        <div className="flex items-center gap-3">
          <span className="h-8 w-8 rounded-full bg-emerald-700 text-white grid place-items-center font-bold text-sm">2</span>
          <div>
            <h3 className="font-display font-bold text-slate-900 text-base">Choose Your Recovery Image</h3>
            <p className="text-xs text-slate-500">Select only one image to represent you</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4">
          {loading && options.avatars.length === 0 && [0,1,2].map((i) => (
            <div key={i} className="h-40 rounded-2xl border border-slate-100 bg-slate-100/60 animate-pulse"/>
          ))}
          {options.avatars.map((a) => {
            const selected = avatarId === a.id;
            const s = styleFor(a.id);
            return (
              <button
                type="button"
                key={a.id}
                onClick={() => setAvatarId(a.id)}
                data-testid={`avatar-${a.id}`}
                aria-pressed={selected}
                className={[
                  "relative rounded-2xl border transition-all p-3 sm:p-4 text-center flex flex-col items-center gap-2 min-h-[7rem]",
                  s.bg, s.borderColor,
                  selected ? `ring-2 ${s.ringColor} border-transparent shadow-md scale-[1.01]` : "hover:shadow-sm hover:-translate-y-0.5",
                ].join(" ")}
              >
                <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-full grid place-items-center text-3xl sm:text-4xl ${s.iconBg}`}>
                  <span aria-hidden>{a.emoji || s.emoji}</span>
                </div>
                <div className="font-bold text-sm text-slate-800 flex items-center gap-1">
                  <span aria-hidden>{a.emoji || s.emoji}</span> {a.label}
                </div>
                <span
                  className={[
                    "mt-1 h-5 w-5 rounded-full grid place-items-center border-2 transition-colors",
                    selected ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 bg-white",
                  ].join(" ")}
                >
                  {selected && <CheckCircle2 size={14} className="text-white"/>}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <div className="mt-6">
        <Button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="personalize-submit"
          className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-base font-bold shadow-sm"
        >
          {busy ? "Saving\u2026" : submitLabel} {!busy && <ArrowRight size={18}/>}
          {!busy && canSubmit && <Sparkles size={16} className="ml-1 opacity-80"/>}
        </Button>
        <p className="mt-3 text-center text-xs text-slate-500 flex items-center justify-center gap-1">
          <Lock size={12}/> Your number is stored securely — never in plain text.
        </p>
      </div>
    </div>
  );
}
